import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Code, Book, Key, Shield, Server, Zap, Sparkles } from 'lucide-react';

export default function APIDocsPage() {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  const apiCategories = [
    {
      id: 'authentication',
      name: 'Authentication',
      icon: Shield,
      description: 'OAuth-based authentication via Emergent platform',
      endpoints: [
        {
          method: 'POST',
          path: '/api/auth/session',
          description: 'Create authentication session from OAuth callback',
          auth: 'None (public)',
          headers: '{"X-Session-ID": "emergent_session_token"}',
          response: '{"user": {...}, "session": {...}}',
          note: 'Sets session_token cookie (httpOnly, 7-day expiry)'
        },
        {
          method: 'GET',
          path: '/api/auth/me',
          description: 'Get current authenticated user',
          auth: 'Required (cookie)',
          response: '{"id": "uuid", "email": "user@example.com", "name": "User Name", ...}'
        },
        {
          method: 'POST',
          path: '/api/auth/logout',
          description: 'Logout and clear session',
          auth: 'Required',
          response: '{"message": "Logged out successfully"}'
        },
        {
          method: 'GET',
          path: '/api/user/role',
          description: 'Get current user role and permissions',
          auth: 'Required',
          response: '{"role": "super_admin", "is_super_admin": true}'
        }
      ]
    },
    {
      id: 'subscription',
      name: 'Subscription & Billing',
      icon: Zap,
      description: 'Manage subscriptions, trials, and usage tracking',
      endpoints: [
        {
          method: 'GET',
          path: '/api/subscription',
          description: 'Get user/company subscription details',
          auth: 'Required',
          response: '{"tier": "professional", "status": "trial", "daily_api_limit": 1000, "trial_end": "2024-11-15T00:00:00Z", ...}'
        },
        {
          method: 'PATCH',
          path: '/api/subscription/upgrade?tier={tier}',
          description: 'Upgrade subscription tier (starter, professional, enterprise)',
          auth: 'Required',
          params: 'tier: string (starter|professional|enterprise)',
          response: '{"success": true, "subscription": {...}}'
        },
        {
          method: 'GET',
          path: '/api/usage/stats',
          description: 'Get API usage statistics (today + last 7 days)',
          auth: 'Required',
          response: '{"today_usage": 150, "daily_limit": 1000, "percentage_used": 15, "last_7_days": [...], ...}'
        },
        {
          method: 'GET',
          path: '/api/usage/check',
          description: 'Check current rate limit status',
          auth: 'Required',
          response: '{"over_limit": false, "usage": 150, "limit": 1000}'
        }
      ]
    },
    {
      id: 'dashboard',
      name: 'Dashboard Data',
      icon: Server,
      description: 'Vessel tracking, ports, bunker prices, freight rates',
      permission: 'Requires VIEW permission for dashboard module',
      endpoints: [
        {
          method: 'GET',
          path: '/api/vessels',
          description: 'List all vessels with positions and status',
          auth: 'Required',
          permission: 'dashboard: view',
          response: '[{"id": "uuid", "name": "Pacific Navigator", "imo": "IMO1234567", "vessel_class": "Panamax", "latitude": 1.29, "longitude": 103.85, "speed": 14.5, "status": "underway", ...}]'
        },
        {
          method: 'GET',
          path: '/api/vessels/{vessel_id}',
          description: 'Get specific vessel details',
          auth: 'Required',
          permission: 'dashboard: view',
          response: '{"id": "uuid", "name": "Pacific Navigator", ...}'
        },
        {
          method: 'GET',
          path: '/api/ports',
          description: 'List all ports with congestion data',
          auth: 'Required',
          permission: 'dashboard: view',
          response: '[{"id": "uuid", "name": "Singapore", "country": "Singapore", "congestion_index": 0.65, "active_vessels": 42, ...}]'
        },
        {
          method: 'GET',
          path: '/api/bunker-prices',
          description: 'List bunker fuel prices by port and type',
          auth: 'Required',
          permission: 'dashboard: view',
          response: '[{"id": "uuid", "port": "Singapore", "fuel_type": "VLSFO", "price_per_mt": 650, "date": "2024-11-01", ...}]'
        },
        {
          method: 'GET',
          path: '/api/freight-rates',
          description: 'List freight rates by shipping lane',
          auth: 'Required',
          permission: 'dashboard: view',
          response: '[{"id": "uuid", "lane": "Pacific-Asia", "route": "USWC to Far East", "rate_per_day": 25000, ...}]'
        },
        {
          method: 'POST',
          path: '/api/seed-data',
          description: 'Generate mock data (10 vessels, 5 ports, 15 bunker prices, 12 freight rates)',
          auth: 'Required',
          response: '{"message": "Mock data generated", "vessels": 10, "ports": 5, ...}'
        }
      ]
    },
    {
      id: 'trade_analytics',
      name: 'Trade Analytics',
      icon: Code,
      description: 'Morning reports, opportunities, and sales alerts',
      permission: 'VIEW for read, EDIT for generate/create',
      endpoints: [
        {
          method: 'GET',
          path: '/api/trade-analytics/morning-reports',
          description: 'List morning reports',
          auth: 'Required',
          permission: 'trade_analytics: view',
          response: '[{"id": "uuid", "report_date": "2024-11-01", "lanes_analysis": [...], "congestion_moves": [...], ...}]'
        },
        {
          method: 'POST',
          path: '/api/trade-analytics/morning-reports/generate',
          description: 'Generate new morning report with lane analysis, congestion, weather',
          auth: 'Required',
          permission: 'trade_analytics: edit',
          response: '{"id": "uuid", "report_date": "2024-11-01", "lanes_analysis": [...], ...}'
        },
        {
          method: 'GET',
          path: '/api/trade-analytics/opportunities',
          description: 'List detected opportunities',
          auth: 'Required',
          permission: 'trade_analytics: view',
          response: '[{"id": "uuid", "opportunity_type": "arbitrage", "fit_score": 87, "estimated_profit": 125000, ...}]'
        },
        {
          method: 'POST',
          path: '/api/trade-analytics/opportunities/scan',
          description: 'Scan for new arbitrage opportunities',
          auth: 'Required',
          permission: 'trade_analytics: edit',
          response: '{"message": "Scanned 3 opportunities", "opportunities": [...]}'
        },
        {
          method: 'GET',
          path: '/api/trade-analytics/alerts',
          description: 'List sales alerts for user',
          auth: 'Required',
          permission: 'trade_analytics: view',
          response: '[{"id": "uuid", "alert_type": "lane_threshold", "is_read": false, ...}]'
        },
        {
          method: 'PATCH',
          path: '/api/trade-analytics/alerts/{alert_id}/read',
          description: 'Mark alert as read',
          auth: 'Required',
          permission: 'trade_analytics: edit',
          response: '{"message": "Alert marked as read"}'
        },
        {
          method: 'POST',
          path: '/api/trade-analytics/alerts/trigger',
          description: 'Generate sample alerts for testing',
          auth: 'Required',
          permission: 'trade_analytics: edit',
          response: '{"message": "3 alerts created"}'
        }
      ]
    },
    {
      id: 'insurance',
      name: 'Freight Insurance',
      icon: Shield,
      description: 'Quotes, risk assessment, and policy management',
      permission: 'VIEW for calculate/read, EDIT for create, FULL_CONTROL for policies',
      endpoints: [
        {
          method: 'POST',
          path: '/api/insurance/quotes/calculate',
          description: 'Calculate insurance premium',
          auth: 'Required',
          permission: 'insurance: view',
          body: '{"vessel_name": "Pacific Navigator", "origin": "Singapore", "destination": "Rotterdam", "coverage_amount": 2000000, "wind_threshold": 50, "delay_threshold": 7}',
          response: '{"premium": 8500, "risk_factors": {...}, "breakdown": {...}}'
        },
        {
          method: 'POST',
          path: '/api/insurance/quotes/create',
          description: 'Save calculated quote',
          auth: 'Required',
          permission: 'insurance: edit',
          response: '{"id": "uuid", "premium": 8500, ...}'
        },
        {
          method: 'GET',
          path: '/api/insurance/quotes',
          description: 'List saved quotes for user',
          auth: 'Required',
          permission: 'insurance: view',
          response: '[{"id": "uuid", "vessel_name": "...", "premium": 8500, ...}]'
        },
        {
          method: 'POST',
          path: '/api/insurance/risk/assess',
          description: 'Assess vessel and route risk',
          auth: 'Required',
          permission: 'insurance: view',
          body: '{"vessel_name": "...", "origin": "...", "destination": "...", "vessel_age": 5}',
          response: '{"vessel_reliability": 85, "route_difficulty": 45, "overall_risk": 35, ...}'
        },
        {
          method: 'GET',
          path: '/api/insurance/risk/assessments',
          description: 'List risk assessments',
          auth: 'Required',
          permission: 'insurance: view'
        },
        {
          method: 'POST',
          path: '/api/insurance/policies/create',
          description: 'Create policy from quote',
          auth: 'Required',
          permission: 'insurance: full_control',
          body: '{"quote_id": "uuid"}',
          response: '{"id": "uuid", "policy_number": "POL-20241101-1234", "status": "active", ...}'
        },
        {
          method: 'GET',
          path: '/api/insurance/policies',
          description: 'List active policies',
          auth: 'Required',
          permission: 'insurance: view'
        },
        {
          method: 'PATCH',
          path: '/api/insurance/policies/{policy_id}/cancel',
          description: 'Cancel active policy',
          auth: 'Required',
          permission: 'insurance: full_control'
        },
        {
          method: 'POST',
          path: '/api/insurance/policies/{policy_id}/simulate-trigger',
          description: 'Simulate parametric trigger event',
          auth: 'Required',
          permission: 'insurance: edit',
          body: '{"trigger_type": "wind_exceeded"}',
          response: '{"triggered": true, "claim_amount": 2000000, ...}'
        }
      ]
    },
    {
      id: 'marketplace',
      name: 'Sagisu Marketplace',
      icon: Server,
      description: 'RFQs, vessel matching, and deal rooms',
      permission: 'VIEW for read, EDIT for create/modify',
      endpoints: [
        {
          method: 'POST',
          path: '/api/marketplace/rfqs/create',
          description: 'Create Request for Quote',
          auth: 'Required',
          permission: 'marketplace: edit',
          params: 'cargo_type, cargo_quantity_mt, vessel_class_required, load_port, discharge_port, laycan_start, laycan_end',
          response: '{"id": "uuid", "rfq_number": "RFQ-20241101-1234", ...}'
        },
        {
          method: 'GET',
          path: '/api/marketplace/rfqs',
          description: 'List user RFQs',
          auth: 'Required',
          permission: 'marketplace: view'
        },
        {
          method: 'POST',
          path: '/api/marketplace/matches/generate/{rfq_id}',
          description: 'Generate vessel matches for RFQ with AI fit scoring',
          auth: 'Required',
          permission: 'marketplace: edit',
          response: '{"message": "Generated 5 matches", "matches_count": 5}'
        },
        {
          method: 'GET',
          path: '/api/marketplace/matches/{rfq_id}',
          description: 'Get vessel matches for RFQ',
          auth: 'Required',
          permission: 'marketplace: view',
          response: '[{"id": "uuid", "vessel_name": "...", "fit_score": 87, "reasoning": [...], ...}]'
        },
        {
          method: 'POST',
          path: '/api/marketplace/deals/create',
          description: 'Create deal room from vessel match',
          auth: 'Required',
          permission: 'marketplace: edit',
          body: '{"rfq_id": "uuid", "vessel_match_id": "uuid"}',
          response: '{"id": "uuid", "status": "negotiation", "progress_stage": 1, ...}'
        },
        {
          method: 'GET',
          path: '/api/marketplace/deals',
          description: 'List active deal rooms',
          auth: 'Required',
          permission: 'marketplace: view'
        },
        {
          method: 'POST',
          path: '/api/marketplace/deals/{deal_id}/messages',
          description: 'Send message in deal room',
          auth: 'Required',
          permission: 'marketplace: edit',
          body: '{"message": "Agreed to terms"}',
          response: '{"id": "uuid", "message": "...", "created_at": "..."}'
        },
        {
          method: 'PATCH',
          path: '/api/marketplace/deals/{deal_id}/status',
          description: 'Update deal stage (Negotiation→Terms→Compliance→Closing)',
          auth: 'Required',
          permission: 'marketplace: edit',
          params: 'status: string, progress_stage: int',
          response: '{"message": "Deal status updated"}'
        }
      ]
    },
    {
      id: 'integrations',
      name: 'Integrations',
      icon: Code,
      description: 'Manage API connections (BYOK - Bring Your Own Keys)',
      endpoints: [
        {
          method: 'GET',
          path: '/api/integrations',
          description: 'List user API connections',
          auth: 'Required',
          response: '{"marinetraffic": {"status": "connected", "last_tested": "..."}, ...}'
        },
        {
          method: 'POST',
          path: '/api/integrations/{integration_id}',
          description: 'Save API credentials (encrypted with AES-128)',
          auth: 'Required',
          body: '{"api_key": "your_api_key"}',
          note: 'Credentials encrypted before storage',
          response: '{"success": true, "message": "Integration connected"}'
        },
        {
          method: 'DELETE',
          path: '/api/integrations/{integration_id}',
          description: 'Disconnect integration',
          auth: 'Required',
          response: '{"success": true}'
        },
        {
          method: 'POST',
          path: '/api/integrations/{integration_id}/test',
          description: 'Test API connection',
          auth: 'Required',
          body: '{"api_key": "test_key"}',
          response: '{"success": true}'
        }
      ]
    },
    {
      id: 'admin',
      name: 'Admin Panel',
      icon: Shield,
      description: 'User, company, and module management (Admin only)',
      permission: 'Super Admin or Company Admin',
      endpoints: [
        {
          method: 'GET',
          path: '/api/admin/users',
          description: 'List all users (Super Admin only)',
          auth: 'Required (Super Admin)',
          response: '[{"id": "uuid", "email": "...", "role": "manager", "company_name": "...", ...}]'
        },
        {
          method: 'POST',
          path: '/api/admin/users/{user_id}/role',
          description: 'Assign role to user',
          auth: 'Required (Super Admin)',
          body: '{"role": "company_admin"}',
          response: '{"success": true}'
        },
        {
          method: 'POST',
          path: '/api/admin/users/{user_id}/company',
          description: 'Assign user to company',
          auth: 'Required (Super Admin)',
          body: '{"company_id": "uuid"}',
          response: '{"success": true}'
        },
        {
          method: 'GET',
          path: '/api/admin/companies',
          description: 'List all companies',
          auth: 'Required (Super Admin)',
          response: '[{"id": "uuid", "name": "Maersk", "subscription_tier": "enterprise", "user_count": 25, ...}]'
        },
        {
          method: 'POST',
          path: '/api/admin/companies',
          description: 'Create new company',
          auth: 'Required (Super Admin)',
          body: '{"name": "Company Name", "subscription_tier": "professional"}',
          response: '{"success": true, "company": {...}}'
        },
        {
          method: 'GET',
          path: '/api/admin/company/users',
          description: 'List users in company admin own company',
          auth: 'Required (Company Admin)',
          response: '[{"id": "uuid", "email": "...", "role": "manager", ...}]'
        },
        {
          method: 'GET',
          path: '/api/admin/company/usage',
          description: 'Company-wide usage statistics',
          auth: 'Required (Company Admin)',
          response: '{"company": {...}, "user_count": 10, "today_usage": 450, "last_7_days": [...], ...}'
        },
        {
          method: 'POST',
          path: '/api/admin/invitations/create',
          description: 'Create user invitation with pre-assigned role and company',
          auth: 'Required (Admin)',
          body: '{"email": "user@company.com", "company_id": "uuid", "role": "manager"}',
          response: '{"success": true, "invite_link": "https://sagisu.com?invite=token", ...}'
        },
        {
          method: 'GET',
          path: '/api/admin/invitations',
          description: 'List pending invitations',
          auth: 'Required (Admin)',
          response: '[{"id": "uuid", "email": "...", "role": "manager", "expires_at": "...", ...}]'
        }
      ]
    },
    {
      id: 'ai',
      name: 'AI Assistant',
      icon: Sparkles,
      description: 'Conversational AI powered by Emergent LLM / Palantir AIP',
      endpoints: [
        {
          method: 'POST',
          path: '/api/ai/chat',
          description: 'Chat with AI assistant (module-specific context)',
          auth: 'Required',
          body: '{"message": "Find opportunities for iron ore", "module": "trade_analytics", "context": {...}, "conversation_history": [...]}',
          response: '{"response": "I found 3 opportunities...", "actions": [{"type": "create_rfq", "label": "Create RFQ", ...}], "provider": "emergent_llm", "model": "gpt-4o"}',
          note: 'Uses Palantir AIP if connected, otherwise Emergent LLM (GPT-4o)'
        }
      ]
    }
  ];

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-[hsl(var(--info))] text-white',
      POST: 'bg-[hsl(var(--success))] text-white',
      PATCH: 'bg-[hsl(var(--warning))] text-white',
      DELETE: 'bg-[hsl(var(--destructive))] text-white'
    };
    return colors[method] || 'bg-[hsl(var(--muted))]';
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="api-docs-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white py-20">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Book className="h-10 w-10" />
              <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>
                API Documentation
              </h1>
            </div>
            <p className="text-xl opacity-90">
              Complete reference for Maritime Pulse REST API - 80+ endpoints for maritime intelligence, 
              trade analytics, insurance, and marketplace operations.
            </p>
            <div className="flex gap-4 mt-6">
              <Badge className="bg-white/20 text-white px-3 py-1">REST API v2</Badge>
              <Badge className="bg-white/20 text-white px-3 py-1">OAuth 2.0</Badge>
              <Badge className="bg-white/20 text-white px-3 py-1">JSON</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Start */}
      <div className="container max-w-7xl mx-auto px-4 py-12">
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Quick Start
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">1. Authentication</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                All API requests require authentication via session cookie obtained through OAuth login.
              </p>
              <div className="bg-[hsl(var(--secondary))] p-4 rounded-md font-mono text-sm">
                <div className="text-[hsl(var(--muted-foreground))]">// Login via OAuth, then use session cookie</div>
                <div>Cookie: session_token=your_session_token</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">2. Base URL</h3>
              <div className="bg-[hsl(var(--secondary))] p-4 rounded-md font-mono text-sm">
                https://sagisu.com/api
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">3. Example Request</h3>
              <div className="bg-[hsl(var(--secondary))] p-4 rounded-md font-mono text-sm overflow-x-auto">
                <div className="text-[hsl(var(--success))]">curl</div> -X GET https://sagisu.com/api/vessels \\<br/>
                &nbsp;&nbsp;-H "Cookie: session_token=your_token" \\<br/>
                &nbsp;&nbsp;-H "Content-Type: application/json"
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">4. Rate Limits</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                <strong>Starter:</strong> 100 API calls/day &nbsp;|&nbsp;
                <strong>Professional:</strong> 1,000 calls/day &nbsp;|&nbsp;
                <strong>Enterprise:</strong> 10,000+ calls/day
              </p>
            </div>
          </CardContent>
        </Card>

        {/* API Categories */}
        <Tabs defaultValue="authentication" className="space-y-8">
          <TabsList className="grid w-full grid-cols-6">
            {apiCategories.map(cat => (
              <TabsTrigger key={cat.id} value={cat.id}>
                {cat.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {apiCategories.map(category => (
            <TabsContent key={category.id} value={category.id} className="space-y-6">
              {/* Category Header */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {React.createElement(category.icon, { className: 'h-5 w-5 text-[hsl(var(--accent))]' })}
                    {category.name}
                  </CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                  {category.permission && (
                    <Badge variant="outline" className="mt-2 w-fit">
                      Permission: {category.permission}
                    </Badge>
                  )}
                </CardHeader>
              </Card>

              {/* Endpoints */}
              {category.endpoints.map((endpoint, idx) => (
                <Card key={idx} data-testid={`endpoint-${endpoint.path}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={getMethodColor(endpoint.method)}>
                          {endpoint.method}
                        </Badge>
                        <code className="text-sm font-mono">{endpoint.path}</code>
                      </div>
                      {endpoint.permission && (
                        <Badge variant="outline" className="text-xs">
                          {endpoint.permission}
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="mt-2">
                      {endpoint.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Authentication:</span>
                        <span className="ml-2 font-medium">{endpoint.auth}</span>
                      </div>
                      {endpoint.permission && (
                        <div>
                          <span className="text-[hsl(var(--muted-foreground))]">Permission:</span>
                          <span className="ml-2 font-medium">{endpoint.permission}</span>
                        </div>
                      )}
                    </div>

                    {endpoint.params && (
                      <div>
                        <div className="text-sm font-semibold mb-2">Parameters:</div>
                        <div className="bg-[hsl(var(--secondary))] p-3 rounded-md text-sm font-mono">
                          {endpoint.params}
                        </div>
                      </div>
                    )}

                    {endpoint.body && (
                      <div>
                        <div className="text-sm font-semibold mb-2">Request Body:</div>
                        <div className="bg-[hsl(var(--secondary))] p-3 rounded-md text-sm font-mono overflow-x-auto">
                          <pre>{endpoint.body}</pre>
                        </div>
                      </div>
                    )}

                    {endpoint.headers && (
                      <div>
                        <div className="text-sm font-semibold mb-2">Headers:</div>
                        <div className="bg-[hsl(var(--secondary))] p-3 rounded-md text-sm font-mono">
                          <pre>{endpoint.headers}</pre>
                        </div>
                      </div>
                    )}

                    {endpoint.response && (
                      <div>
                        <div className="text-sm font-semibold mb-2">Response:</div>
                        <div className="bg-[hsl(var(--secondary))] p-3 rounded-md text-sm font-mono overflow-x-auto">
                          <pre>{endpoint.response}</pre>
                        </div>
                      </div>
                    )}

                    {endpoint.note && (
                      <div className="bg-[hsl(var(--info))]/10 border border-[hsl(var(--info))] p-3 rounded-md">
                        <p className="text-sm text-[hsl(var(--info))]">{endpoint.note}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          ))}
        </Tabs>

        {/* Error Codes */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle>Error Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b pb-2">
                <span className="font-mono">200 OK</span>
                <span className="text-[hsl(var(--muted-foreground))]">Successful request</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-mono">400 Bad Request</span>
                <span className="text-[hsl(var(--muted-foreground))]">Invalid parameters or request body</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-mono">401 Unauthorized</span>
                <span className="text-[hsl(var(--muted-foreground))]">Missing or invalid session token</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-mono">403 Forbidden</span>
                <span className="text-[hsl(var(--muted-foreground))]">Insufficient permissions for module or action</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-mono">404 Not Found</span>
                <span className="text-[hsl(var(--muted-foreground))]">Resource does not exist</span>
              </div>
              <div className="flex justify-between">
                <span className="font-mono">500 Internal Server Error</span>
                <span className="text-[hsl(var(--muted-foreground))]">Server error, contact support</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support */}
        <Card className="mt-8">
          <CardContent className="py-8">
            <div className="text-center space-y-3">
              <h3 className="text-lg font-semibold">Need Help?</h3>
              <p className="text-[hsl(var(--muted-foreground))]">
                Contact your platform administrator or check the README.md for detailed guides.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
